import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { mealPlanAssoc } from '@/app/components/search/utils/buildDealCards/createHotelDetailDeeplink.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
// import { getDeepLinkId } from '@/app/components/search/Janus/utils';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { type FARE_TYPE, getAirlineId, getRouteId } from '@/app/common/starTrack/events/utils/resultClickedUtils.ts';
import { getTripIdFromDealCard } from '@/app/common/starTrack/events/utils/startrackEventsUtils.ts';
import { createTotalPriceFareType } from '@/app/common/starTrack/events/utils/eventsUtils.ts';

export const RESULT_CLICKED = 'RESULT_CLICKED';
export const RESULT_CLICKED_VERSION = '4.0';

export type ResultClickedPayload = {
  searchId: string;
  vcSearchId: string;
  internalHotelId: number;
  clickId: string;
  tripId: string;
  page: number;
  position: number;
  mealPlanClass: string;
  ctaName: string;
  currency: string;
  totalPrice: number;
  accommodationType: string;
  flashSalesFlag: boolean;
  freeCancellation: boolean;
  providerId: string;
  fareType: FARE_TYPE | null;
  strikeThroughPrice: number;
  outboundAirlineId: string | null;
  inboundAirlineId: string | null;
  outboundRouteId: string | null;
  inboundRouteId: string | null;
  totalPriceEuro: number;
  hotelLabel: string;
  strikethroughTotalPriceEuro: number;
};

export type ResultClickedParams = {
  searchParams: SearchParams;
  dealCard: DealCard;
  clickId: string;
  vcSearchId?: string;
  localization: Localization;
  ctaName: string;
};

export const createResultClickedPayload = ({
  searchParams,
  dealCard,
  clickId,
  vcSearchId,
  localization,
  ctaName,
}: ResultClickedParams): ResultClickedPayload => {
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const vcSearchIdToUse = vcSearchId || searchParams[URLParameters.VC_SEARCH_ID] || '';

  // The deal card and localization are cast from JSON, never validated, so every field RESULT_CLICKED-4.0
  // marks required is guarded below: undefined would be dropped by JSON.stringify and NaN sent as null.
  const currency = localization?.currency || '';
  const pageParam = parseInt(`${searchParams[URLParameters.PAGE]}`, 10);
  const page = Number.isNaN(pageParam) ? 1 : pageParam;
  const position = dealCard.index ?? 0;
  const mealPlan = dealCard?.product?.rate?.features?.mealPlan;
  const mealPlanClass = mealPlanAssoc[`${mealPlan}`] || '';
  const totalPrice = dealCard.product?.rate?.price?.total?.price ?? 0;
  const accommodationType = dealCard.product?.accommodation?.type?.toUpperCase() || '';
  const flashSalesFlag = dealCard?.product?.promotions?.flashSale || false;
  const freeCancellation = dealCard.product?.rate?.features?.cancellation ?? false;
  const providerId = `${dealCard?.product?.accommodation?.providerId ?? ''}`;
  const strikeThroughPrice = dealCard.product?.rate?.price?.total?.strikethrough ?? 0;
  const strikethroughTotalPriceEuro = dealCard.product?.rate?.price?.total?.strikethroughEur ?? 0;
  const transportData = dealCard?.transport?.data;
  const transportId = dealCard?.product?.transport?.transportId;
  const totalPriceEuro = dealCard?.product?.rate?.price?.total?.priceEur ?? 0;
  const tripId = getTripIdFromDealCard(dealCard);
  const facilities = dealCard.product?.accommodation?.facilities;
  const hotelLabel =
    facilities?.familyBabySitting ||
    facilities?.familyChildrenClubs ||
    facilities?.familyChildrenMeal ||
    facilities?.familyChildrenPool
      ? `Family`
      : facilities?.adultsOnly
        ? 'Couple'
        : '';
  return {
    searchId: searchId || '',
    vcSearchId: vcSearchIdToUse,
    internalHotelId: dealCard?.product?.accommodation?.hotelId ?? 0,
    clickId: clickId || '',
    tripId,
    page,
    position,
    mealPlanClass,
    ctaName,
    currency,
    totalPrice,
    accommodationType,
    flashSalesFlag,
    freeCancellation,
    providerId,
    fareType: createTotalPriceFareType(!!dealCard.product?.rate?.flags?.loyalty, dealCard.promotionTheme),
    strikeThroughPrice,
    outboundAirlineId: getAirlineId(
      transportData?.find((transport) => transport.transportId === transportId)?.outbound?.segments
    ),
    inboundAirlineId: getAirlineId(
      transportData?.find((transport) => transport.transportId === transportId)?.inbound?.segments
    ),
    outboundRouteId: getRouteId(
      transportData?.find((transport) => transport.transportId === transportId)?.outbound?.segments
    ),
    inboundRouteId: getRouteId(
      transportData?.find((transport) => transport.transportId === transportId)?.inbound?.segments
    ),
    totalPriceEuro,
    hotelLabel,
    strikethroughTotalPriceEuro,
  };
};
